// The store-subscription audio driver (client-audio spec R1-noop/R2/R3/R4/R8-driver, design
// DD2/DD3/DD10; tasks.md 5.2). One `useClientStore.subscribe(prev→state)` edge detector that
// turns BEST-EFFORT store edges into SFX cues and drives the season/tier ambient bed. It reads
// only the store snapshot (plus the app-root route enter/exit signal wired in group 7); it
// never reads or writes `client/src/canvas/*` (owned by `pixel-art-pass`).
//
// Why identity dedup, not reference/length diff (DD2, spec R2): `MatchState`/`ColonyMeta`/
// `PromptStatus` are LATEST-WINS COALESCED per subscriber under backpressure (PROTOCOL §6), so
// intermediate values can be overwritten before reaching the store, and a reconnect can
// redeliver an already-observed value with a fresh reference. The driver therefore keeps a small
// per-cue-family IDENTITY BASELINE (the last-observed identity) and diffs against it — a new
// identity fires one cue, a repeat/echo fires nothing. The baseline is updated on EVERY observed
// snapshot, so a later unrelated store write (e.g. a per-frame world update, which touches no cue
// family) re-fires nothing.
//
// Baseline lifecycle (R3): the FIRST observed snapshot seeds the baseline and fires no SFX (no
// replay of "everything that already happened" when the layer arms mid-match); a
// `connectionEpoch` change RE-SEEDS the baseline silently (no catch-up burst across the reconnect
// discontinuity), then edge detection resumes on subsequent same-epoch snapshots.
//
// Ambient bed activation is STATE-SYNCHRONIZED, not edge-gated (DD3, spec R4): beyond the
// season/tier *change* crossfades, the driver requests the CURRENT season/tier bed once on engine
// arming (`armEngine`, the NullAudioEngine → WebAudioEngine swap) and on an in-match route-entry
// signal (`routeEntered`) — otherwise a screen-exit `stopAll()` or a mid-season unlock would leave
// the bed silent until an unrelated transition. This is orthogonal to the R3 first-snapshot rule,
// which suppresses historical SFX cues but never the continuous bed reflecting present state.
//
// Teardown (DD10, spec R8-driver): `stopAll()` on match end (`matchResult` null → non-null) and on
// an in-match-screen route-exit signal (`routeExited`), the latter fed from the app-root route
// observer because current-screen identity is router-owned, not store-derived. `stopAll()` is
// idempotent (spec R8-engine), so the normal-completion path — where both triggers coincide —
// needs no driver-side coordination.

import type { ClientStoreState } from '../state/store';
import type { PromptStatus, PromptStatusKind, Season, Tier } from '../net/wire/messages';
import { Tier as TierEnum } from '../net/wire/messages';
import type { AudioEngine, SfxCue } from './engine';

/**
 * The per-colony absolute living-count delta magnitude (between two OBSERVED snapshots) at or
 * above which a coarse `activity` cue fires (DD3, spec R2). A PRESENTATION tunable owned by the
 * audio layer — never a sim/balance value (D10) — and exported so tests key off the constant
 * rather than a hard-coded number. The edge is the PER-STEP delta magnitude (the baseline updates
 * every snapshot), so sub-threshold jitter never accumulates across steps into a spurious cue.
 */
export const ACTIVITY_DELTA_THRESHOLD = 8;

/** The public driver handle. `onSnapshot` is the edge detector `initAudio` (group 7) wires to
 * `store.subscribe`; `armEngine`/`routeEntered`/`routeExited` are the out-of-band signals the
 * mount feeds it. */
export interface AudioDriver {
  /** Observe one store snapshot. The first call (or the first after a `connectionEpoch` change)
   * seeds the baseline and fires nothing; subsequent same-epoch calls diff identities and fire
   * best-effort cues + ambient crossfades. In production this is the `store.subscribe` callback;
   * tests feed synthetic `prev → next` pairs. */
  onSnapshot(state: ClientStoreState): void;
  /** Swap in the armed engine (NullAudioEngine → WebAudioEngine on the first unlock) and request
   * the current season/tier bed once, so the bed is audible immediately on unlock (DD3/R4). */
  armEngine(engine: AudioEngine): void;
  /** An in-match-screen route-entry signal: re-synchronize the current season/tier bed once
   * (symmetric to `routeExited`'s `stopAll()`), firing no historical SFX (R4/R8). */
  routeEntered(): void;
  /** An in-match-screen route-exit signal: stop the bed and every voice (R8). */
  routeExited(): void;
  /** Stop processing further snapshots/signals; idempotent. The engine + store-subscription
   * teardown are owned by `initAudio` (group 7), not the driver. */
  dispose(): void;
}

export interface AudioDriverDeps {
  /** The current audio engine — a `NullAudioEngine` before the first unlock (every call a no-op,
   * spec R1) and a `WebAudioEngine` after (swapped via `armEngine`). Tests inject the recording
   * fake. */
  engine: AudioEngine;
}

/** The last-observed identity per cue family (DD2). Diffing against this — not against array
 * length or object reference — is the only correct reading of a coalesced latest-wins stream. */
interface Baseline {
  /** The connection epoch this baseline belongs to; a change re-seeds silently (R3). */
  readonly epoch: number;
  /** The accumulated set of eliminated colony ids (monotonic within an epoch). */
  readonly eliminations: Set<number>;
  /** typeId → the last-observed prompt `(kind, tick)` identity string. */
  readonly promptByType: Map<number, string>;
  /** typeId → the last-observed `activeVersion`. */
  readonly versionByType: Map<number, number>;
  /** The last-observed season, or undefined when no `MatchState` has been observed. */
  readonly season: Season | undefined;
  /** The last-observed confirmed zoom tier (may be null before the first confirmation). */
  readonly tier: Tier | null;
  /** colonyId → the last-observed living count (the per-step activity baseline). */
  readonly livingByColony: Map<number, number>;
  /** Whether a non-null `matchResult` had been observed (the match-end latch). */
  readonly matchResultPresent: boolean;
}

/** The prompt-lifecycle SFX voice for a status kind, or undefined for kinds with no cue
 * (`idle`/`stale` are lifecycle-internal and silent). `accepted` and `failed` are distinct
 * voices (spec R2). */
function promptCue(kind: PromptStatusKind['kind']): SfxCue | undefined {
  switch (kind) {
    case 'generating':
      return 'prompt-generating';
    case 'repairing':
      return 'prompt-repairing';
    case 'accepted':
      return 'prompt-accepted';
    case 'failed':
      return 'prompt-failed';
    default:
      return undefined; // idle / stale — no voice
  }
}

/** The `(kind, tick)` identity string for a prompt status — the dedup key within a type
 * (the full identity is `(typeId, kind, tick)`, typeId being the map key). */
function promptIdentity(status: PromptStatus): string {
  return `${status.status.kind}:${status.tick}`;
}

/** The season currently known from a snapshot, or undefined when no `MatchState` is present. */
function seasonOf(state: ClientStoreState): Season | undefined {
  return state.matchState?.state.season;
}

/** Extract a fresh baseline from a full snapshot (used to seed at boot and to re-seed on an
 * epoch change). Captures every cue family's current identity so the next diff fires only for
 * genuine new edges. */
function extractBaseline(state: ClientStoreState): Baseline {
  const eliminations = new Set<number>(state.matchState?.state.eliminations ?? []);
  const promptByType = new Map<number, string>();
  for (const [typeId, status] of Object.entries(state.promptStatusByType)) {
    promptByType.set(Number(typeId), promptIdentity(status));
  }
  const versionByType = new Map<number, number>();
  for (const type of state.colonyMeta?.types ?? []) {
    versionByType.set(type.typeId, type.activeVersion);
  }
  const livingByColony = new Map<number, number>();
  for (const row of state.matchState?.state.scoreboard ?? []) {
    livingByColony.set(row.colonyId, row.livingCount);
  }
  return {
    epoch: state.connectionEpoch,
    eliminations,
    promptByType,
    versionByType,
    season: seasonOf(state),
    tier: state.confirmedTier,
    livingByColony,
    matchResultPresent: state.matchResult !== null,
  };
}

/**
 * Build the store-subscription audio driver (design DD2/DD3/DD10). Holds the per-cue-family
 * baseline and the current engine, diffs each observed snapshot, and fires best-effort SFX +
 * ambient bed requests through the injected `AudioEngine` interface.
 */
export function createAudioDriver(deps: AudioDriverDeps): AudioDriver {
  let engine: AudioEngine = deps.engine;
  let baseline: Baseline | undefined;
  let disposed = false;
  // Route presence: whether the driver is on an in-match screen (Colony/Match). Independent of
  // MATCH LIVENESS (`baseline.matchResultPresent`) — audio emits only when BOTH hold, so a
  // completed match stays quiescent even if an in-match route is re-entered (browser Back to
  // Summary → Colony) or audio arms late (implementation-review F1 + confirmation follow-up).
  // `onScreen` DEFAULTS true so driver-unit snapshots (which never signal a route) still fire.
  // `routeExited`/`routeEntered` toggle it; `armEngine` does NOT touch it (arming is not a route
  // event). It never re-activates a dead match — `activateBed` gates on match liveness.
  let onScreen = true;

  /** Request the CURRENT season/tier bed once (arming / route re-entry activation, DD3/R4). A
   * no-op until a `MatchState` (hence a season) has been observed, and — crucially — only for a
   * LIVE match: a completed match (`matchResultPresent`) never restarts its bed on a late arm or a
   * post-Summary in-match re-entry (implementation-review F1 confirmation follow-up). The confirmed
   * tier defaults to `Wide` before the first confirmation. */
  function activateBed(): void {
    if (baseline === undefined || baseline.season === undefined || baseline.matchResultPresent) {
      return;
    }
    engine.ambient({ season: baseline.season, tier: baseline.tier ?? TierEnum.Wide });
  }

  /** Diff one snapshot against the baseline, firing cues + ambient requests, then advance the
   * baseline to the snapshot. Precondition: `baseline` is defined and on the same epoch. */
  function detectEdges(state: ClientStoreState, prev: Baseline): void {
    // --- match-end teardown (R8): the null → non-null latch stops the bed AND goes quiescent —
    // no other edge on the terminal snapshot (a coincident season turn, elimination, version, or
    // scoreboard swing) may restart audio into Lobby/Summary (R8 intent; implementation-review
    // F1). `matchResultPresent` latches in the advanced baseline.
    if (!prev.matchResultPresent && state.matchResult !== null) {
      engine.stopAll();
      return;
    }

    // --- emit cues only while on an in-match screen AND the match is live. Off-screen (a route
    // exit stopped the bed) a store edge must not restart audio (R8 "does not keep droning
    // off-screen"); a completed match (`matchResult` non-null) stays quiescent even on-screen
    // (implementation-review F1). The baseline still advances (below), so a later in-match
    // re-entry never replays the edges missed while suppressed.
    if (!onScreen || state.matchResult !== null) {
      return;
    }

    // --- eliminations by colony-id set membership (R2). A new id fires the any-colony cue; the
    // own id additionally fires the own sting. A reorder/length change with no new id is silent.
    const eliminations = state.matchState?.state.eliminations ?? [];
    for (const id of eliminations) {
      if (!prev.eliminations.has(id)) {
        engine.sfx('elimination');
        if (id === state.colonyId) {
          engine.sfx('own-elimination');
        }
      }
    }

    // --- prompt lifecycle by (typeId, kind, tick) identity (R2). Only the OBSERVED terminal of a
    // coalesced cycle fires (the intermediates never reached the store); a redelivery is silent.
    for (const [typeIdKey, status] of Object.entries(state.promptStatusByType)) {
      const typeId = Number(typeIdKey);
      const identity = promptIdentity(status);
      if (prev.promptByType.get(typeId) !== identity) {
        const cue = promptCue(status.status.kind);
        if (cue !== undefined) {
          engine.sfx(cue);
        }
      }
    }

    // --- hot-swap / rollback by (typeId, activeVersion) (R2): fire on a change from the
    // last-observed version (a rollback to an earlier value is still a change); a redelivered or
    // unchanged version is silent.
    for (const type of state.colonyMeta?.types ?? []) {
      if (prev.versionByType.get(type.typeId) !== type.activeVersion) {
        engine.sfx('version');
      }
    }

    // --- season / tier (R2 season sting + R4 ambient bed). A season TURN (both endpoints known,
    // differing) fires the sting; a season turn OR a tier change requests the target bed (with the
    // NEW season+tier), so if both change in one step only ONE bed request goes out.
    const curSeason = seasonOf(state);
    const curTier = state.confirmedTier;
    const seasonTurn = prev.season !== undefined && curSeason !== undefined && curSeason !== prev.season;
    // The season becoming KNOWN for the first time (no `MatchState` until now — an arm or an
    // in-match route entry that happened before the first `MatchState`) is a bed-ACTIVATION
    // trigger, not a season *turn*: it starts the bed with the current season/tier but fires NO
    // season sting, so a live armed match is never left silent until a later season/tier change
    // even when `confirmedTier` was established by a keyframe before the season arrived
    // (implementation-review F1 confirmation follow-up #2, R4 "audible immediately").
    const seasonFirstKnown = prev.season === undefined && curSeason !== undefined;
    const tierChanged = curTier !== prev.tier;
    if (seasonTurn) {
      engine.sfx('season');
    }
    if (curSeason !== undefined && (seasonTurn || tierChanged || seasonFirstKnown)) {
      engine.ambient({ season: curSeason, tier: curTier ?? TierEnum.Wide });
    }

    // --- coarse activity by per-step per-colony living-count swing (R2/DD3): direction-agnostic,
    // thresholded, and evaluated against the PREVIOUS observed count (not a fixed origin), so a
    // small oscillation never accumulates into a cue. A colony absent from the prior snapshot has
    // no delta and only establishes its baseline.
    for (const row of state.matchState?.state.scoreboard ?? []) {
      const previousCount = prev.livingByColony.get(row.colonyId);
      if (previousCount !== undefined && Math.abs(row.livingCount - previousCount) >= ACTIVITY_DELTA_THRESHOLD) {
        engine.sfx('activity');
      }
    }
  }

  return {
    onSnapshot(state: ClientStoreState): void {
      if (disposed) {
        return;
      }
      // First snapshot, or a reconnect discontinuity: (re-)seed the baseline and fire nothing
      // (R3) — no catch-up burst for state that already existed / the epoch gap.
      if (baseline === undefined || baseline.epoch !== state.connectionEpoch) {
        baseline = extractBaseline(state);
        return;
      }
      detectEdges(state, baseline);
      baseline = extractBaseline(state);
    },

    armEngine(next: AudioEngine): void {
      if (disposed) {
        return;
      }
      engine = next;
      // Arming is not a route event — it does not change `onScreen`; `activateBed` starts the bed
      // only for a live match (so a late arm into a completed match stays silent).
      activateBed();
    },

    routeEntered(): void {
      if (disposed) {
        return;
      }
      onScreen = true;
      activateBed(); // re-sync the bed — but only if the match is still live (checked inside)
    },

    routeExited(): void {
      if (disposed) {
        return;
      }
      onScreen = false;
      engine.stopAll();
    },

    dispose(): void {
      disposed = true;
    },
  };
}
